import React, { useState, createContext, useContext, useMemo, useEffect } from 'react';

import CRMApi from '../Api/CRM/CRMApi';
import GetAllLanguagesResponse from '../Api/CRM/Responses/LanguagesResponse';
import LoggedAdmin from '../Api/Models/LoggedAdmin';

import { GlobalTableSettings } from './elements/common/types';
import { SelectOptions } from './elements/formElements/Select';
import { createOptions } from './helpers/formUtils';

type Dispatch<T> = React.Dispatch<React.SetStateAction<T>>;
type PageScrollerItem = { name: string; element: HTMLDivElement };

export type GlobalState = Readonly<{
    languages: GetAllLanguagesResponse;
    setLanguages: Dispatch<GetAllLanguagesResponse>;
    languageNames: SelectOptions;
    baseLanguageId: number | null;
    loggedAdmin: LoggedAdmin;
    setLoggedAdmin: Dispatch<LoggedAdmin>;
    pageScrollerList: PageScrollerItem[];
    setPageScrollerList: Dispatch<PageScrollerItem[]>;
    tableSettings: GlobalTableSettings;
    setTableSettings: Dispatch<GlobalTableSettings>;
}>;

const Context = createContext({} as GlobalState);

export function GlobalStateProvider({ children }: { readonly children: React.ReactNode }) {
    const [languages, setLanguages] = useState<GetAllLanguagesResponse>([]);
    const languageNames = createOptions(languages);
    const baseLanguageId = languages[0]?.id;
    const [loggedAdmin, setLoggedAdmin] = useState<LoggedAdmin>(null);
    const [pageScrollerList, setPageScrollerList] = useState<PageScrollerItem[]>([]);
    const [tableSettings, setTableSettings] = useState<GlobalTableSettings>({});

    const state: GlobalState = {
        languages,
        setLanguages,
        languageNames,
        baseLanguageId,
        loggedAdmin,
        setLoggedAdmin,
        pageScrollerList,
        setPageScrollerList,
        tableSettings,
        setTableSettings,
    };

    useEffect(() => initializeGlobalState(state), [loggedAdmin]);

    const memoizedState = useMemo(() => state, [languages, loggedAdmin, pageScrollerList, tableSettings]);

    return <Context.Provider value={memoizedState}>{children}</Context.Provider>;
}

export default function useGlobalState() {
    return useContext(Context);
}

function initializeGlobalState(state: GlobalState) {
    if (!state.loggedAdmin) {
        CRMApi.get('me')
            .then((response) => state.setLoggedAdmin(new LoggedAdmin(response.data)))
            .catch(() => {
                // To prevent console error when api returns 401 Unauthorized status (not logged in)
            });
        return;
    }

    if (!state.languages.length) {
        CRMApi.languages.getAll().then(state.setLanguages);
    }
}
